import { StanFloatingMenu } from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { StanInput } from '@/components';
import { memo, useCallback, useMemo, useRef } from 'react';
import IconChevronDown from '@/assets/icons/ic-chevron-down.svg';
import { findTimezoneOption, getDefaultTimezoneOption, TIMEZONE_OPTIONS, TimezoneOption } from './timezone.logic';
import { TimezoneRow } from './TimezoneRow';

export interface StanTimezoneSelectorProps {
    /** Currently selected timezone (e.g., 'America/New_York') */
    selectedTimezone: string;
    /** Callback when timezone changes */
    onTimezoneChange: (timezone: string) => void;
    /** Label for the input field and its position */
    label?: {
        value: string;
        position: 'inner' | 'top';
    };
    /** Whether the input has an error */
    error?: boolean;
    /** Hint text to display below the input */
    hintText?: string;
    /** Whether the selector is disabled */
    disabled?: boolean;
    /** ID for the input element */
    id?: string;
    /** Name for the input element */
    name?: string;
}

// Memoized timezone list component to prevent re-renders
const TimezoneList = memo(
    ({
        options,
        selectedValue,
        onSelect,
        closeMenu,
    }: {
        options: TimezoneOption[];
        selectedValue: string;
        onSelect: (timezone: string) => void;
        closeMenu: () => void;
    }) => {
        const handleClick = useCallback(
            (option: TimezoneOption) => {
                const timezone = option.zones[0];
                if (timezone) {
                    onSelect(timezone);
                }
                closeMenu();
            },
            [onSelect, closeMenu]
        );

        return (
            <div className='flex-1 overflow-y-auto'>
                {options.map(option => (
                    <TimezoneRow
                        key={option.value}
                        iso={option.abbr}
                        showName={option.showName}
                        offset={option.offset}
                        isSelected={selectedValue === option.value}
                        onClick={() => handleClick(option)}
                    />
                ))}
            </div>
        );
    }
);

TimezoneList.displayName = 'TimezoneList';

const StanTimezoneSelectorComponent = ({
    selectedTimezone,
    onTimezoneChange,
    error = false,
    label,
    hintText,
    disabled = false,
    id = 'timezone-selector',
    name = 'timezone-selector',
}: StanTimezoneSelectorProps) => {
    const currentValue = useMemo(
        () => findTimezoneOption(selectedTimezone) || getDefaultTimezoneOption(),
        [selectedTimezone]
    );

    // Store the callback in a ref to avoid recreating handlers
    const onChangeRef = useRef(onTimezoneChange);
    onChangeRef.current = onTimezoneChange;

    const stableOnChange = useCallback((timezone: string) => {
        onChangeRef.current(timezone);
    }, []);

    return (
        <div className='max-w-full'>
            <StanFloatingMenu
                name={`${name}-menu`}
                placement='bottom-start'
                matchTriggerWidth
                trigger={
                    <div className='cursor-pointer'>
                        <StanInput
                            id={id}
                            name={name}
                            topLabel={label?.position === 'top' ? label.value : undefined}
                            innerLabel={label?.position === 'inner' ? label.value : undefined}
                            value={currentValue.label}
                            onChange={() => {}}
                            readOnly
                            error={error}
                            hintText={hintText}
                            disabled={disabled}
                            endIcon={IconChevronDown}
                        />
                    </div>
                }
            >
                {({ closeMenu }) => (
                    <div className='flex max-h-96 w-full flex-col'>
                        <TimezoneList
                            options={TIMEZONE_OPTIONS}
                            selectedValue={currentValue.value}
                            onSelect={stableOnChange}
                            closeMenu={closeMenu}
                        />
                    </div>
                )}
            </StanFloatingMenu>
        </div>
    );
};

export const StanTimezoneSelector = memo(StanTimezoneSelectorComponent);
